import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="st2heeqiaph5">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="86w7kxrkyi3h">
      <BrowserRouter data-id="element-2" data-mp="86ckz3qzj5j4">
        <ScrollToTop data-id="element-3" data-mp="3gle7gc7darc0" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-4" data-mp="3gkv2dw2tfxfe">
          <SkipLink data-id="element-5" data-mp="3bmthr6pqrq1t" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-6" data-mp="3ljpiurdb99wc" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-7" data-mp="2xtbyln0ywfcb">
            <Header data-id="element-8" data-mp="2sw21dl7c6zdx" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-9" data-mp="2suquvdmibdj4">
            <Routes data-id="element-10" data-mp="18bok0lajc9wf">
              <Route path="/" element={<Home data-id="element-12" data-mp="1d4rdyuj85ld5" />} data-id="element-11" data-mp="1d47w1dtkqwz4" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-14" data-mp="1d6atgix23sdv" />} data-id="element-13" data-mp="1d62jaysbj16d" />
              <Route path="/pricing" element={<PricingPage data-id="element-16" data-mp="yh6a2yeizi46" />} data-id="element-15" data-mp="ygd4w1v7t0o5" />
              <Route path="/faq" element={<FAQPage data-id="element-18" data-mp="13dws3r9w4ns8" />} data-id="element-17" data-mp="13eod685v3349" />
              <Route path="/contact" element={<Contact data-id="element-20" data-mp="13cbwdybuayss" />} data-id="element-19" data-mp="13f7vhpzfbtit" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-22" data-mp="1r4sg0db86yu7" />} data-id="element-21" data-mp="1r3r4k7qasaby" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-24" data-mp="1w2kh9xxrfxb2" />} data-id="element-23" data-mp="1w3c2qfxn8enm" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-26" data-mp="1i319554y8lyr" />} data-id="element-25" data-mp="1i444bp9b1akh" />
              <Route path="/gracias" element={<GraciasPage data-id="element-28" data-mp="1m4eezf81kp5o" />} data-id="element-27" data-mp="1m561m0jnvcjt" />
              <Route path="/guia" element={<GuiaPage data-id="element-30" data-mp="6ofdunwudtex" />} data-id="element-29" data-mp="1m3cukk54yqba" />
              <Route path="/pedido" element={<PedidoPage data-id="element-32" data-mp="6mujozefwchl" />} data-id="element-31" data-mp="6o5xls1ln0mf" />
              <Route path="/foto" element={<FotoPage data-id="element-34" data-mp="aqis1kawbc3w" />} data-id="element-33" data-mp="6qs4dpgo1c3l" />
            </Routes>
          </main>
          <Footer data-id="element-35" data-mp="ano77rnyzp9y" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-36" data-mp="qygjpx44du12" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}